import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, Chip, Tabs, Tab,
  Card, CardContent, Grid, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CommentIcon from '@mui/icons-material/Comment';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StorageIcon from '@mui/icons-material/Storage';
import LanguageIcon from '@mui/icons-material/Language';
import ShareIcon from '@mui/icons-material/Share';
import HubIcon from '@mui/icons-material/Hub';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';
import CommentThread from '../components/CommentThread';
import VersionHistory from '../components/VersionHistory';
import PermissionEditor from '../components/PermissionEditor';
import AIPanel from '../components/AIPanel';
import ClassificationBadge from '../components/ClassificationBadge';
import QualityScore from '../components/QualityScore';
import ShareDialog from '../components/ShareDialog';
import VersionDiff from '../components/VersionDiff';
import EntityTags from '../components/EntityTags';
import AgentCopilot from '../components/AgentCopilot';
import AgentReviewDialog from '../components/AgentReviewDialog';
import DocumentWorkflowPanel from '../components/DocumentWorkflowPanel';
import { applyDocumentPatches } from '../utils/applyManualPatches';
import { decodeUserFromToken, isDmsAdmin, isPlatformAdmin } from '../utils/dmsPermissions';
import CreateIcon from '@mui/icons-material/Create';

const STATUS_COLORS = {
  draft: 'default',
  review: 'warning',
  published: 'success',
  archived: 'error',
};

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType) {
  return mimeType && mimeType.startsWith('image/');
}

function isPdf(mimeType) {
  return mimeType === 'application/pdf';
}

function isDocx(mimeType) {
  return mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || mimeType === 'application/msword';
}

function isText(mimeType) {
  return mimeType && (mimeType.startsWith('text/') || mimeType === 'application/json');
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function DocumentDetail({ documentId: propDocumentId, onBack, onNavigate }) {
  const MP = useDmsPalette();
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const documentId = propDocumentId || paramId;
  const handleBack = onBack || (() => navigate(-1));
  const handleNavigate = onNavigate || ((navObj) => {
    if (navObj?.view === 'document-detail' && navObj.documentId) {
      navigate(`/document/${navObj.documentId}`);
    } else if (navObj?.view === 'search') {
      navigate(`/search${navObj.query ? `?q=${encodeURIComponent(navObj.query)}` : ''}`);
    }
  });

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showAI, setShowAI] = useState(false);

  // Share dialog (F4)
  const [shareOpen, setShareOpen] = useState(false);

  // Version diff dialog (G1)
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffVersion, setDiffVersion] = useState(null);

  // Related documents (H3)
  const [relatedDocs, setRelatedDocs] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  // Edit metadata dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '', tags: '', status: 'draft', classification: 'unclassified' });
  const [editSaving, setEditSaving] = useState(false);
  const [publishReview, setPublishReview] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [forcePublishing, setForcePublishing] = useState(false);
  const [user] = useState(() => decodeUserFromToken(localStorage.getItem('token')));

  // Delete confirm dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Blob preview URL for PDF/images (avoids auth issues with iframe src)
  const [previewUrl, setPreviewUrl] = useState(null);
  // DOCX → HTML preview
  const [docxHtml, setDocxHtml] = useState(null);
  // Plain text preview
  const [textContent, setTextContent] = useState(null);

  const loadDocument = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dmsApi.getDocument(documentId);
      const document = data.document || data;
      setDoc(document);
      setEditForm({
        title: document.title || '',
        description: document.description || '',
        tags: (document.tags || []).join(', '),
        status: document.status || 'draft',
        classification: document.classification || 'unclassified',
      });
    } catch (err) {
      setError(err.error || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { loadDocument(); }, [loadDocument]);

  // Load related documents (H3)
  useEffect(() => {
    let cancelled = false;
    const fetchRelated = async () => {
      setRelatedLoading(true);
      try {
        const data = await dmsApi.getRelatedDocuments(documentId);
        if (!cancelled) setRelatedDocs(data.documents || data.related || data || []);
      } catch {
        if (!cancelled) setRelatedDocs([]);
      } finally {
        if (!cancelled) setRelatedLoading(false);
      }
    };
    fetchRelated();
    return () => { cancelled = true; };
  }, [documentId]);

  // Fetch file as blob for inline preview (PDF/images/DOCX/text)
  useEffect(() => {
    if (!doc) return;
    const mime = doc.mimeType || '';
    const needsBlob = isPdf(mime) || isImage(mime) || isDocx(mime) || isText(mime);
    if (!needsBlob) return;

    let revoked = false;
    const fetchBlob = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${dmsApi.downloadUrl(documentId)}?token=${token}`);
        if (!resp.ok) throw new Error('Download failed');
        const blob = await resp.blob();
        if (revoked) return;

        if (isPdf(mime) || isImage(mime)) {
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
        } else if (isDocx(mime)) {
          const mammoth = await import('mammoth/mammoth.browser');
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          if (!revoked) setDocxHtml(result.value);
        } else if (isText(mime)) {
          const text = await blob.text();
          if (!revoked) setTextContent(text);
        }
      } catch (e) {
        console.warn('Preview fetch failed:', e);
      }
    };
    fetchBlob();
    return () => { revoked = true; if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, documentId]);

  const handleDownload = () => {
    const token = localStorage.getItem('token');
    const url = `${dmsApi.downloadUrl(documentId)}?token=${token}`;
    window.open(url, '_blank');
  };

  const saveDocument = async (payload, forcePublish = false) => {
    setEditSaving(true);
    try {
      const res = await dmsApi.updateDocument(documentId, { ...payload, forcePublish });
      const updated = res.document || res;
      setEditOpen(false);
      setReviewDialogOpen(false);
      setPublishReview(null);
      if (updated) setDoc(updated);
      loadDocument();
      return true;
    } catch (err) {
      if (err.publishReview) {
        setPublishReview(err.publishReview);
        setReviewDialogOpen(true);
        return false;
      }
      setError(err.error || 'Failed to update document');
      return false;
    } finally {
      setEditSaving(false);
      setForcePublishing(false);
    }
  };

  const handleEditSave = async () => {
    await saveDocument({
      title: editForm.title,
      description: editForm.description,
      tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      status: editForm.status,
      classification: editForm.classification,
    });
  };

  const handleForcePublish = async () => {
    setForcePublishing(true);
    try {
      const res = await dmsApi.publishDocument(documentId, { forcePublish: true });
      setReviewDialogOpen(false);
      setPublishReview(null);
      setEditOpen(false);
      if (res.document) setDoc(res.document);
      loadDocument();
    } catch (err) {
      if (err.publishReview) {
        setPublishReview(err.publishReview);
      } else {
        setError(err.error || 'Force publish failed');
      }
    } finally {
      setForcePublishing(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await dmsApi.deleteDocument(documentId);
      setDeleteOpen(false);
      handleBack();
    } catch (err) {
      setError(err.error || 'Failed to delete document');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !doc) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>Back</Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!doc) return null;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Top Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={handleBack} sx={{ color: MP.textSecondary }}>
          <ArrowBackIcon />
        </IconButton>
        <InsertDriveFileIcon sx={{ color: MP.blue }} />
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap title={doc.title}>
          {doc.title}
        </Typography>
        <Chip
          label={doc.status}
          color={STATUS_COLORS[doc.status] || 'default'}
          size="small"
          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
        />
        <Chip
          label={doc.vault}
          size="small"
          variant="outlined"
          sx={{ borderColor: MP.blue, color: MP.blue, textTransform: 'capitalize' }}
        />
        <ClassificationBadge classification={doc.classification} />
      </Box>

      <Grid container spacing={3}>
        {/* Left Column: Viewer + Tabs */}
        <Grid item xs={12} md={showAI ? 7 : 8}>
          {/* Document Viewer */}
          <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}`, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              {isPdf(doc.mimeType) ? (
                previewUrl ? (
                  <Box sx={{ width: '100%', height: { xs: 300, sm: 400, md: 500 }, borderRadius: 1, overflow: 'hidden' }}>
                    <iframe
                      src={previewUrl}
                      title={doc.title}
                      style={{ width: '100%', height: '100%', border: 'none', borderRadius: 4 }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6, color: MP.textSecondary }}>
                    <CircularProgress size={32} sx={{ mb: 1 }} />
                    <Typography variant="body2">Loading PDF preview…</Typography>
                  </Box>
                )
              ) : isImage(doc.mimeType) ? (
                <Box sx={{ textAlign: 'center' }}>
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={doc.title}
                      style={{ maxWidth: '100%', borderRadius: 4, objectFit: 'contain' }}
                      className="doc-preview-img"
                    />
                  ) : (
                    <CircularProgress size={32} />
                  )}
                </Box>
              ) : isDocx(doc.mimeType) ? (
                docxHtml ? (
                  <Box
                    sx={{
                      maxHeight: { xs: 400, md: 600 },
                      overflowY: 'auto',
                      p: 3,
                      bgcolor: '#fff',
                      borderRadius: 1,
                      color: '#111',
                      fontFamily: 'Georgia, serif',
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      '& h1,& h2,& h3,& h4': { mt: 2, mb: 1 },
                      '& p': { mb: 1 },
                      '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                      '& td,& th': { border: '1px solid #ccc', p: '4px 8px' },
                    }}
                    dangerouslySetInnerHTML={{ __html: docxHtml }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6, color: MP.textSecondary }}>
                    <CircularProgress size={32} sx={{ mb: 1 }} />
                    <Typography variant="body2">Loading document preview…</Typography>
                  </Box>
                )
              ) : isText(doc.mimeType) ? (
                textContent !== null ? (
                  <Box
                    component="pre"
                    sx={{
                      maxHeight: { xs: 400, md: 600 },
                      overflowY: 'auto',
                      p: 2,
                      bgcolor: MP.deepBlue,
                      borderRadius: 1,
                      fontSize: '0.82rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: MP.textPrimary,
                      m: 0,
                    }}
                  >
                    {textContent}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6, color: MP.textSecondary }}>
                    <CircularProgress size={32} sx={{ mb: 1 }} />
                    <Typography variant="body2">Loading preview…</Typography>
                  </Box>
                )
              ) : (
                <Box sx={{
                  textAlign: 'center', py: 6, color: MP.textSecondary,
                  bgcolor: MP.deepBlue, borderRadius: 1,
                }}>
                  <InsertDriveFileIcon sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Preview not available for {doc.mimeType || 'this file type'}
                  </Typography>
                  <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                    Download to View
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons — scrollable row on mobile */}
          <Box sx={{
            display: 'flex', gap: 1, mb: 2, flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
            pb: { xs: 0.5, md: 0 },
            '& .MuiButton-root': { minWidth: 'auto', flexShrink: 0 },
            scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
          }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} size="small">
              Download
            </Button>
            <Button
              variant="outlined" startIcon={<ShareIcon />}
              onClick={() => setShareOpen(true)} size="small"
              sx={{ color: MP.blue, borderColor: MP.blue, '&:hover': { borderColor: MP.blue, bgcolor: `${MP.blue}10` } }}
            >
              Share
            </Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)} size="small">
              Edit
            </Button>
            {(doc.mimeType === 'application/vnd.soldikeeper.document+json'
              || doc.tags?.includes('structured-editor')
              || doc.mimeType === 'application/json') && doc.status !== 'archived' && (
              <Button
                variant="outlined"
                startIcon={<CreateIcon />}
                onClick={() => navigate(`/editor/${documentId}`)}
                size="small"
                sx={{ color: MP.green, borderColor: MP.green }}
              >
                Open in Editor
              </Button>
            )}
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setActiveTab(1)} size="small">
              Version
            </Button>
            {isPlatformAdmin(user) && (
              <Button
                variant="outlined" startIcon={<AutoAwesomeIcon />}
                onClick={() => setShowAI(!showAI)} size="small"
                sx={{ color: MP.green, borderColor: MP.green, '&:hover': { borderColor: MP.green, bgcolor: `${MP.green}10` } }}
              >
                {showAI ? 'Hide AI' : 'AI'}
              </Button>
            )}
            <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
            <Button
              variant="outlined" color="error" startIcon={<DeleteIcon />}
              onClick={() => setDeleteOpen(true)} size="small"
            >
              Delete
            </Button>
          </Box>

          {/* Tabs */}
          <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}` }}>
            <Tabs
              value={activeTab} onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: `1px solid ${MP.border}`, px: 2 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<CommentIcon />} iconPosition="start" label="Comments" sx={{ textTransform: 'none', minHeight: 48 }} />
              <Tab icon={<HistoryIcon />} iconPosition="start" label="Versions" sx={{ textTransform: 'none', minHeight: 48 }} />
              <Tab icon={<LockIcon />} iconPosition="start" label="Permissions" sx={{ textTransform: 'none', minHeight: 48 }} />
              {isPlatformAdmin(user) && (
                <Tab icon={<AutoAwesomeIcon />} iconPosition="start" label="AI" sx={{ textTransform: 'none', minHeight: 48 }} />
              )}
              <Tab icon={<HubIcon />} iconPosition="start" label="Related" sx={{ textTransform: 'none', minHeight: 48 }} />
            </Tabs>
            <Box sx={{ p: 2 }}>
              <TabPanel value={activeTab} index={0}>
                <CommentThread documentId={documentId} />
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <VersionHistory
                  documentId={documentId}
                  onCompare={(ver) => { setDiffVersion(ver); setDiffOpen(true); }}
                />
              </TabPanel>
              <TabPanel value={activeTab} index={2}>
                <PermissionEditor documentId={documentId} />
              </TabPanel>
              <TabPanel value={activeTab} index={3}>
                <AIPanel document={doc} />
              </TabPanel>
              <TabPanel value={activeTab} index={4}>
                <RelatedPanel
                  docs={relatedDocs}
                  loading={relatedLoading}
                  onNavigate={handleNavigate}
                />
              </TabPanel>
            </Box>
          </Card>
        </Grid>

        {/* Right Column: Metadata + optional AI */}
        <Grid item xs={12} md={showAI ? 5 : 4}>
          <DocumentWorkflowPanel
            document={doc}
            user={user}
            onDocumentUpdate={(updated) => {
              if (updated) setDoc(updated);
              loadDocument();
            }}
          />

          {/* Metadata Panel */}
          <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}`, mb: 2, mt: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: MP.textSecondary }}>
                Document Info
              </Typography>

              <Box sx={{ mb: 1.5 }}>
                <QualityScore doc={doc} size="large" showSuggestions={true} />
              </Box>

              {doc.description && (
                <Typography variant="body2" sx={{ mb: 2, color: MP.white, lineHeight: 1.6 }}>
                  {doc.description}
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <MetaRow icon={<PersonIcon />} label="Author" value={doc.createdBy?.name || doc.createdBy?.email || 'Unknown'} />
                <MetaRow icon={<CalendarTodayIcon />} label="Created" value={doc.createdAt ? format(new Date(doc.createdAt), 'MMM d, yyyy · h:mm a') : '—'} />
                <MetaRow icon={<CalendarTodayIcon />} label="Updated" value={doc.updatedAt ? format(new Date(doc.updatedAt), 'MMM d, yyyy · h:mm a') : '—'} />
                <MetaRow icon={<StorageIcon />} label="Size" value={formatFileSize(doc.fileSize || doc.size)} />
                <MetaRow icon={<InsertDriveFileIcon />} label="Type" value={doc.mimeType || '—'} />
                <MetaRow icon={<HistoryIcon />} label="Version" value={`v${doc.version || 1}`} />
                {doc.language && (
                  <MetaRow icon={<LanguageIcon />} label="Language" value={doc.language} />
                )}
              </Box>

              {/* Tags */}
              {doc.tags && doc.tags.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Tags</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {doc.tags.map(tag => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Entity Tags (H2) */}
              {doc.entities && doc.entities.length > 0 && (
                <EntityTags
                  entities={doc.entities}
                  onEntityClick={(label) => {
                    handleNavigate({ view: 'search', query: label });
                  }}
                />
              )}
            </CardContent>
          </Card>

          {/* AI Panel (side) */}
          {showAI && <AIPanel document={doc} />}
        </Grid>
      </Grid>

      {/* Edit Metadata Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Document Metadata</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title" fullWidth value={editForm.title}
            onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))}
          />
          <TextField
            label="Description" fullWidth multiline rows={3} value={editForm.description}
            onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))}
          />
          <TextField
            label="Tags (comma-separated)" fullWidth value={editForm.tags}
            onChange={(e) => setEditForm(p => ({ ...p, tags: e.target.value }))}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={editForm.status}
              onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))}
              label="Status"
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Classification</InputLabel>
            <Select
              value={editForm.classification || 'unclassified'}
              onChange={(e) => setEditForm(p => ({ ...p, classification: e.target.value }))}
              label="Classification"
            >
              {['unclassified', 'internal', 'confidential', 'restricted', 'secret'].map(c => (
                <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>
                  {c === 'secret' ? '🔐 SECRET' : c === 'restricted' ? '🔒 Restricted' : c === 'confidential' ? '🛡️ Confidential' : c === 'internal' ? '🏢 Internal' : '🌐 Unclassified'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>&quot;{doc.title}&quot;</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog (F4) */}
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        document={doc}
      />

      {/* Version Diff Dialog (G1) */}
      <VersionDiff
        version1={diffVersion}
        version2={diffOpen ? doc : null}
        onClose={() => { setDiffOpen(false); setDiffVersion(null); }}
      />

      {isPlatformAdmin(user) && (
        <AgentCopilot
          mode="document"
          content={{ title: doc.title, description: doc.description, extractedText: doc.extractedText, tags: doc.tags }}
          classification={doc.classification}
          documentId={documentId}
          collapsed
          onApplyDocumentPatches={(patches) => setEditForm((prev) => applyDocumentPatches(prev, patches))}
          onReviewComplete={(review) => { setPublishReview(review); setReviewDialogOpen(true); }}
        />
      )}

      <AgentReviewDialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        review={publishReview}
        onForcePublish={isDmsAdmin(user) ? handleForcePublish : null}
        publishing={forcePublishing}
      />
    </Box>
  );
}

/* Small helper component for metadata rows */
function MetaRow({ icon, label, value }) {
  const MP = useDmsPalette();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color: MP.textSecondary, display: 'flex' }}>
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 65 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{value}</Typography>
    </Box>
  );
}

/* Related Documents panel rendered inside the Related tab (H3) */
function RelatedPanel({ docs, loading, onNavigate }) {
  const MP = useDmsPalette();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} variant="rounded" height={72} sx={{ bgcolor: MP.deepBlue }} />
        ))}
      </Box>
    );
  }

  if (!docs || docs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5, color: MP.textSecondary }}>
        <HubIcon sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="body2">No related documents found.</Typography>
      </Box>
    );
  }

  const displayDocs = docs.slice(0, 5);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {displayDocs.map((related) => (
        <Card
          key={related._id}
          onClick={() => onNavigate?.({ view: 'document-detail', documentId: related._id })}
          sx={{
            bgcolor: MP.deepBlue,
            border: `1px solid ${MP.border}`,
            cursor: 'pointer',
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: MP.blue },
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <InsertDriveFileIcon sx={{ fontSize: 18, color: MP.blue, mt: 0.1, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap title={related.title}>
                  {related.title}
                </Typography>
                {related.reason && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {related.reason}
                  </Typography>
                )}
              </Box>
              {related.classification && (
                <ClassificationBadge classification={related.classification} />
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
